import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AlertTriangle, Menu, X, Heart, LogOut, Plane, Bell, ShieldCheck, Gift, Rocket } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";

export default function Header() {
    const { user, logout } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);

    const NAV = [
        { to: "/at-risk", label: t("nav.atRisk"), urgent: true, testid: "nav-at-risk" },
        { to: "/dogs", label: t("nav.dogs"), testid: "nav-dogs" },
        { to: "/resources", label: "For Rescues", icon: ShieldCheck, testid: "nav-resources" },
        { to: "/ways-to-give", label: "Ways to Give", icon: Gift, testid: "nav-ways-to-give" },
        { to: "/rescues", label: t("nav.rescues"), testid: "nav-rescues" },
        { to: "/shelters", label: t("nav.shelters"), testid: "nav-shelters" },
        { to: "/transport", label: t("nav.transport"), icon: Plane, testid: "nav-transport" },
        { to: "/matchmaking", label: t("nav.aiMatch"), testid: "nav-matchmaking" },
        { to: "/community", label: t("nav.community"), testid: "nav-community" },
    ];

    return (
        <header className="glass-header sticky top-0 z-50" data-testid="site-header">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="logo-home-link">
                    <div className="w-9 h-9 rounded-full bg-[var(--rl-primary)] flex items-center justify-center text-white shadow-sm">
                        <Heart className="w-4.5 h-4.5" fill="white" strokeWidth={0} size={18} />
                    </div>
                    <span className="font-serif text-2xl tracking-tight text-[var(--rl-text)]">RescueLink</span>
                </Link>

                <nav className="hidden xl:flex items-center gap-0.5" data-testid="main-nav">
                    {NAV.map((item) => {
                        const Icon = item.icon;
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={item.testid}
                                className={({ isActive }) =>
                                    `px-2.5 py-2 rounded-full text-[13px] font-medium transition-colors ${
                                        isActive
                                            ? "bg-[var(--rl-cream)] text-[var(--rl-text)]"
                                            : "text-[var(--rl-text-2)] hover:text-[var(--rl-text)] hover:bg-[var(--rl-cream)]/60"
                                    } ${item.urgent ? "!text-[var(--rl-urgent)]" : ""}`
                                }
                            >
                                {item.urgent && <AlertTriangle className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />}
                                {Icon && <Icon className="inline w-3.5 h-3.5 -mt-0.5 mr-1" />}
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden xl:flex items-center gap-1.5 shrink-0">
                    <NotificationBell />
                    <LanguageSwitcher />
                    <Link to="/join">
                        <Button variant="outline" className="rounded-full h-9 px-3.5 text-xs font-semibold border-[var(--rl-primary)] text-[var(--rl-primary)] hover:bg-[#fff5f3]" data-testid="header-join-btn">
                            <Rocket className="w-3.5 h-3.5 mr-1.5" /> Register Rescue
                        </Button>
                    </Link>
                    <Link to="/donate">
                        <Button className="btn-primary rounded-full px-4 h-9" data-testid="donate-btn-header">
                            <Heart className="w-3.5 h-3.5 mr-1.5" /> {t("nav.donate")}
                        </Button>
                    </Link>
                    {user ? (
                        <div className="flex items-center gap-1.5">
                            <Link to="/dashboard" className="flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-[var(--rl-cream)] transition-colors" data-testid="user-menu">
                                <div className="w-7 h-7 rounded-full bg-[var(--rl-sage)] flex items-center justify-center text-[var(--rl-text)] text-xs font-semibold">
                                    {user.name?.[0]?.toUpperCase() || "U"}
                                </div>
                            </Link>
                            <button onClick={() => { logout(); nav("/"); }} className="p-1.5 rounded-full hover:bg-[var(--rl-cream)]" data-testid="logout-btn" title="Logout">
                                <LogOut className="w-4 h-4 text-[var(--rl-text-2)]" />
                            </button>
                        </div>
                    ) : (
                        <Link to="/auth" data-testid="login-link">
                            <Button variant="ghost" className="rounded-full h-9 px-3 text-sm">{t("nav.signin")}</Button>
                        </Link>
                    )}
                </div>

                <div className="flex xl:hidden items-center gap-2">
                    <NotificationBell />
                    <LanguageSwitcher />
                    <button className="p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden border-t border-[var(--rl-border)] bg-[var(--rl-surface)]" data-testid="mobile-menu">
                    <div className="px-6 py-4 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `px-3 py-2.5 rounded-md text-sm ${isActive ? "bg-[var(--rl-cream)]" : ""} ${item.urgent ? "text-[var(--rl-urgent)]" : "text-[var(--rl-text)]"}`
                                }>
                                {item.label}
                            </NavLink>
                        ))}
                        <div className="border-t border-[var(--rl-border)] mt-2 pt-3 flex flex-col gap-2">
                            <Link to="/join" onClick={() => setOpen(false)}>
                                <Button variant="outline" className="w-full rounded-full border-[var(--rl-primary)] text-[var(--rl-primary)]" data-testid="mobile-join-btn">
                                    <Rocket className="w-4 h-4 mr-2" /> Register your rescue
                                </Button>
                            </Link>
                            <Link to="/donate" onClick={() => setOpen(false)}>
                                <Button className="btn-primary w-full rounded-full">{t("nav.donate")}</Button>
                            </Link>
                            {user ? (
                                <>
                                    <Link to="/dashboard" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full rounded-full">My Dashboard</Button>
                                    </Link>
                                    <Button variant="ghost" onClick={() => { logout(); setOpen(false); nav("/"); }}>Sign out</Button>
                                </>
                            ) : (
                                <Link to="/auth" onClick={() => setOpen(false)}>
                                    <Button variant="outline" className="w-full rounded-full">{t("nav.signin")}</Button>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
